'use client';

import { useReducedMotion } from 'framer-motion';
import type { CSSProperties, ElementType } from 'react';
import { memo, useMemo } from 'react';

import { cn } from '@/lib/utils';

type ShimmerProps = {
  children: string;
  as?: ElementType;
  className?: string;
  duration?: number;
  spread?: number;
};

/**
 * Shimmer component provides an animated shimmer effect that sweeps across text.
 * Perfect for indicating loading states or drawing attention to dynamic content.
 * Inspired by Vercel AI Elements.
 */
function ShimmerComponent({
  children,
  as: Component = 'p',
  className,
  duration = 2,
  spread = 2,
}: ShimmerProps) {
  const shouldReduceMotion = useReducedMotion();

  const dynamicSpread = useMemo(() => children.length * spread, [children, spread]);

  if (shouldReduceMotion) {
    return (
      <Component className={cn('inline-block text-muted-foreground', className)}>
        {children}
      </Component>
    );
  }

  // A CSS animation sweeps the highlight, so no script runs on each frame.
  return (
    <Component
      className={cn(
        'relative inline-block animate-shimmer-sweep bg-[length:250%_100%,auto] bg-clip-text text-transparent',
        '[--bg:linear-gradient(90deg,#0000_calc(50%-var(--spread)),hsl(var(--background)),#0000_calc(50%+var(--spread)))]',
        '[background-repeat:no-repeat,padding-box]',
        className,
      )}
      style={
        {
          '--spread': `${dynamicSpread}px`,
          '--shimmer-duration': `${duration}s`,
          'color': 'transparent',
          'WebkitBackgroundClip': 'text',
          'backgroundImage':
            'var(--bg), linear-gradient(hsl(var(--muted-foreground)), hsl(var(--muted-foreground)))',
        } as CSSProperties
      }
    >
      {children}
    </Component>
  );
}

export const Shimmer = memo(ShimmerComponent);
